import type { Bar, Section, Song, TabNote } from './types';

export function isPlayableSong(value: unknown): value is Song {
  if (!isRecord(value)) return false;
  if (typeof value.title !== 'string') return false;
  if (typeof value.artist !== 'string') return false;
  if (!isPositiveFinite(value.bpm)) return false;
  if (!isPositiveInteger(value.beatsPerBar)) return false;
  if (!Array.isArray(value.bars) || value.bars.length === 0) return false;
  if (!Array.isArray(value.sections) || value.sections.length === 0) return false;
  const bars = value.bars;
  const sections = value.sections;
  if (!bars.every(isBar)) return false;
  if (!sections.every((section) => isSection(section, bars.length))) return false;
  return true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPositiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && (value as number) > 0;
}

function isBar(value: unknown): value is Bar {
  if (!isRecord(value)) return false;
  if (value.chord !== undefined && typeof value.chord !== 'string') return false;
  if (value.section !== undefined && typeof value.section !== 'string') return false;
  if (!Array.isArray(value.notes)) return false;
  return value.notes.every(isTabNote);
}

function isSection(value: unknown, barCount: number): value is Section {
  if (!isRecord(value)) return false;
  if (typeof value.name !== 'string' || value.name.trim() === '') return false;
  if (!Number.isInteger(value.barOffset) || (value.barOffset as number) < 0 || (value.barOffset as number) >= barCount) {
    return false;
  }
  if (!Array.isArray(value.chords) || !value.chords.every((chord) => typeof chord === 'string')) return false;
  if (value.lyrics !== undefined && (!Array.isArray(value.lyrics) || !value.lyrics.every((line) => typeof line === 'string'))) {
    return false;
  }
  return true;
}

function isTabNote(value: unknown): value is TabNote {
  if (!isRecord(value)) return false;
  if (![1, 2, 3, 4, 5, 6].includes(value.string as number)) return false;
  if (!(typeof value.fret === 'number' || value.fret === 'x')) return false;
  if (typeof value.beat !== 'number' || !Number.isFinite(value.beat) || value.beat < 0) return false;
  if (value.finger !== undefined && ![1, 2, 3, 4, 'T'].includes(value.finger as string | number)) return false;
  return true;
}
